import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { FundsSummary } from "@/utils/accounting";
import type { FundKey } from "@/types";
import NumberInput from "@/components/base/NumberInput";
import { formatCOP } from "@/utils/format";

interface TransferFundsModalProps {
  open: boolean;
  funds: FundsSummary;
  onClose: () => void;
  onSubmit: (input: { fromFund: FundKey; toFund: FundKey; amount: number; note: string }) => void;
}

// Mueve saldo manualmente entre dos fondos (ej. de Fondo Asesor a Caja Menor).
const TransferFundsModal = ({ open, funds, onClose, onSubmit }: TransferFundsModalProps) => {
  const options = funds.funds;
  const [fromFund, setFromFund] = useState(options[0]?.key ?? "caja");
  const [toFund, setToFund] = useState(options[1]?.key ?? "asesor");
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setFromFund(options[0]?.key ?? "caja");
    setToFund(options[1]?.key ?? "asesor");
    setAmount(0);
    setNote("");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const from = funds.funds.find((f) => f.key === fromFund);

  const submit = () => {
    if (fromFund === toFund) {
      setError("El fondo de origen y destino deben ser diferentes.");
      return;
    }
    const value = Number(amount) || 0;
    if (value <= 0) {
      setError("El monto debe ser mayor a cero.");
      return;
    }
    const available = from?.balance ?? 0;
    if (value > available) {
      setError(
        `El fondo de origen solo tiene ${formatCOP(available)}. No puedes transferir más de su saldo.`
      );
      return;
    }
    onSubmit({ fromFund, toFund, amount: value, note: note.trim() });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transferir entre fondos"
      icon="ri-exchange-2-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-exchange-2-line" /> Transferir
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Fondo de origen</label>
          <select
            value={fromFund}
            onChange={(e) => setFromFund(e.target.value)}
            className={inputClass}
          >
            {options.map((f) => (
              <option key={f.key} value={f.key} className="bg-background-100">
                {f.short} · {formatCOP(f.balance)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Fondo de destino</label>
          <select value={toFund} onChange={(e) => setToFund(e.target.value)} className={inputClass}>
            {options.map((f) => (
              <option key={f.key} value={f.key} className="bg-background-100">
                {f.short} · {formatCOP(f.balance)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Monto (COP)</label>
          <NumberInput min={0} value={amount} onChange={setAmount} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Motivo</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ej: Cubrir compra de inventario"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-3 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
        <p className="text-[11px] text-foreground-500">
          Saldo disponible en origen:{" "}
          <span className="font-semibold text-foreground-900">{formatCOP(from?.balance ?? 0)}</span>
        </p>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default TransferFundsModal;